'use client'

import {type RefObject, useLayoutEffect} from 'react'

// `backwards`: an animation started inside a frame callback can begin a moment ahead of that frame; until then the
// panel keeps its previous height instead of painting, or being measured at, the new one.
const RESIZE_TIMING: KeyframeAnimationOptions = {duration: 300, easing: 'cubic-bezier(0.32, 0.72, 0, 1)', fill: 'backwards'}
const CONTINUOUS_CHANGE_MS = 50
const SIZE_TRANSITIONS = new Set(['grid-template-rows', 'height', 'max-height'])
const HANDOFF_MS = 1500

let handoff: {height: number; tabFocused: boolean; at: number} | null = null

/**
 * A sibling section's editor is about to replace the dialog holding `from` (e.g. a tab rendered by its own route).
 * The next resuming dialog opens at this dialog's height and resizes from there, so the swap reads as one dialog.
 */
export function handOffEditorHeight(from: Element) {
    const panel = from.closest('[data-document-editor]')?.firstElementChild
    const focused = document.activeElement
    handoff = panel instanceof HTMLElement ? {
        height: panel.offsetHeight,
        tabFocused: Boolean(focused?.matches('[role="tab"]') && panel.contains(focused)),
        at: performance.now(),
    } : null
}

// Not consumed on read: StrictMode and Activity replay the opening effects, and every replay starts from the same height.
export const readEditorHandoff = () => handoff && performance.now() - handoff.at < HANDOFF_MS ? handoff : null

/** Content that transitions its own height (e.g. Reveal) already moves smoothly; the panel follows it. */
const hasSizeTransition = (root: HTMLElement) => root.getAnimations({subtree: true})
    .some(animation => animation instanceof CSSTransition && SIZE_TRANSITIONS.has(animation.transitionProperty))

/**
 * Animates a dialog panel's height when its editor content grows or shrinks.
 * Content rows inside the scroll owner keep their intrinsic size while the
 * panel height is pinned by the animation, so they reveal every new target.
 */
export function useSmoothPanelResize(panelRef: RefObject<HTMLElement | null>, enabled: boolean, resumeHandoff = false) {
    // Before paint, so a resumed dialog never shows a frame at its own height first.
    useLayoutEffect(() => {
        const panel = panelRef.current
        const flow = panel?.querySelector<HTMLElement>('[data-editor-scroll]')
        if (!enabled || !panel || !flow || typeof ResizeObserver === 'undefined' || !panel.animate) return
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        let shown = panel.offsetHeight
        let animation: Animation | null = null
        let lastRowChange = -Infinity
        let rowsReplacedAt = -Infinity
        let frame: number | null = null
        const resumed = resumeHandoff ? readEditorHandoff() : null
        // The first delivery reports the rows just observed; the resumed animation already covers them.
        let skipDelivery = Boolean(resumed)
        // The height last painted, mid-animation included; `shown` holds an animation's target instead.
        let painted: number | null = null
        const resizePanel = () => {
            // The row change is consumed here; later panel-only frames belong to the animation it starts.
            rowsReplacedAt = -Infinity
            const from = animation?.playState === 'running' ? panel.offsetHeight : shown
            animation?.cancel()
            const to = panel.offsetHeight
            shown = to
            // Rows resizing on consecutive frames animate themselves; follow them directly.
            const now = performance.now()
            const continuous = now - lastRowChange < CONTINUOUS_CHANGE_MS
            lastRowChange = now
            if (continuous || Math.abs(to - from) < 1 || hasSizeTransition(flow)) return
            animation = panel.animate({height: [`${from}px`, `${to}px`]}, RESIZE_TIMING)
        }
        const observer = new ResizeObserver(entries => {
            const panelEntry = entries.find(entry => entry.target === panel)
            if (panelEntry) painted = panelEntry.borderBoxSize[0]?.blockSize ?? panelEntry.contentRect.height
            if (skipDelivery) {
                skipDelivery = false
                return
            }
            const rowsReplaced = performance.now() - rowsReplacedAt < CONTINUOUS_CHANGE_MS
            // Viewport, keyboard and our own animation frames resize only the panel; follow them.
            if (!rowsReplaced && entries.every(entry => entry.target === panel)) {
                shown = panel.offsetHeight
                return
            }
            // Our own animation just changed the rows' fit (a scrollbar in a flow that follows the panel): same cycle, not new content.
            if (frame !== null) return
            // Starts before this frame paints, or the new height shows for one frame. The panel sits this cycle out:
            // the height it is about to change would go undelivered and raise a ResizeObserver loop error.
            observer.unobserve(panel)
            resizePanel()
            frame ??= requestAnimationFrame(() => {
                frame = null
                observer.observe(panel)
            })
        })
        const observeRows = () => {
            for (const row of flow.children) observer.observe(row)
        }
        // A footer or body mounted after open, or removed, is a content change too.
        const rows = new MutationObserver(() => {
            rowsReplacedAt = performance.now()
            observeRows()
        })
        observer.observe(panel)
        observeRows()
        rows.observe(flow, {childList: true})
        if (resumed) {
            shown = resumed.height
            resizePanel()
            // Content arriving right after the swap (a section's own data) retargets this animation instead of jumping.
            lastRowChange = -Infinity
        }
        return () => {
            // A placeholder replaced mid-resize by the section it waited for hands on the height shown now. An effect
            // replay before any paint (StrictMode) keeps the original height; a newer hand-off already has its own.
            if (resumed && resumed === handoff && painted !== null && animation?.playState === 'running') {
                handoff = {...resumed, height: painted, at: performance.now()}
            }
            rows.disconnect()
            observer.disconnect()
            if (frame !== null) cancelAnimationFrame(frame)
            animation?.cancel()
        }
    }, [enabled, panelRef, resumeHandoff])
}
